// src/ui/ReactionKit.jsx — the reaction button, its tray, the summary on the
// right of a post and the "who reacted" sheet, for every web surface that can
// react: the feed, profiles and pages, the post pane and the reels pane.
// The phone's twin is src/components/ReactionKit.tsx; the rules they share
// (hit-testing the tray, the summary, the tabs) are services/reactions.
//
// ── THE GESTURES ─────────────────────────────────────────────────────────────
//   mouse   hover the heart ~0.45s → the tray; hover a face (it grows, its name
//           shows) and click it. A click on the heart is a plain like.
//   touch   hold the heart ~0.25s → the tray; keep the finger down and slide —
//           let go on a face to pick it, anywhere else to cancel ("Release to
//           cancel" is laid over the action row meanwhile).
// 🟥 THE HEART HAS `touch-action: none`. Without it the browser turns a slide
//    across the tray into a scroll and cancels the pointer half way.
//
// ── THE FACES ────────────────────────────────────────────────────────────────
// Google's Noto animated emoji (CC BY 4.0, credited in About), served from
// /reactions: the Lottie file plays in the tray and ONCE when you pick it; the
// still SVG everywhere else. The Lottie files load on first use only.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Lottie from 'lottie-react';
import { REACTIONS, REACTION_LABEL, trayHit, reactionSummary, reactionTabs } from '../services/reactions';
import { fetchLikers } from '../services/likeApi';
import { compactViews } from '../services/postGrid';
import Pic from './VibeAvatar';
import { Heart, X } from './icons';

const BASE = `${process.env.PUBLIC_URL || ''}/reactions`;
export const stillOf = (r) => `${BASE}/${r}.svg`;

/* one fetch per face, shared by every tray and button on the page */
const animCache = new Map();
function loadAnim(r) {
  if (!animCache.has(r)) {
    animCache.set(r, fetch(`${BASE}/${r}.json`).then((res) => (res.ok ? res.json() : null)).catch(() => null));
  }
  return animCache.get(r);
}
function useAnim(r) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let live = true;
    if (r) loadAnim(r).then((d) => { if (live) setData(d); });
    return () => { live = false; };
  }, [r]);
  return data;
}

/** A face, standing still. */
export function ReactionIcon({ r, size = 18, className = '', style }) {
  return <img src={stillOf(r)} alt="" width={size} height={size} className={`rx-icon ${className}`} style={style} draggable={false} />;
}

/** A face, moving — looping (the tray) or once (a pick). The still until it has loaded. */
function ReactionAnim({ r, size, loop, onDone }) {
  const data = useAnim(r);
  if (!data) return <ReactionIcon r={r} size={size} />;
  return (
    <span className="rx-anim" style={{ width: size, height: size }}>
      <Lottie animationData={data} loop={loop} autoplay onComplete={onDone} style={{ width: size * 1.2, height: size * 1.2 }} />
    </span>
  );
}

/** The heart's face: an outline when I have not reacted, my reaction when I have — played once per `play`. */
export function ReactionGlyph({ mine, size = 18, play = 0 }) {
  const [playing, setPlaying] = useState(false);
  const seen = useRef(play);
  useEffect(() => {
    if (play !== seen.current) { seen.current = play; if (mine) setPlaying(true); }
  }, [play, mine]);
  if (!mine) return <Heart width={size} height={size} />;
  return playing
    ? <ReactionAnim key={`${mine}-${play}`} r={mine} size={size + 4} loop={false} onDone={() => setPlaying(false)} />
    : <ReactionIcon r={mine} size={size + 2} />;
}

const HOVER_OPEN_MS = 450;
const HOVER_CLOSE_MS = 300;
const HOLD_MS = 250;
const SLOP = 8;

/**
 * The heart with its tray. `onTap()` likes / un-likes; `onPick(r)` is a
 * reaction. `onHold({ open, hover })` reports a finger holding the tray open
 * (for "Release to cancel"). `placement` = 'above' | 'left' (the reels rail).
 */
export function ReactButton({ mine, onTap, onPick, onHold, placement = 'above', dark = false, className = '', label, children }) {
  const [open, setOpen] = useState(null);          // null | 'hover' | 'hold'
  const [hover, setHover] = useState(-1);
  const tray = useRef(null);
  const btn = useRef(null);
  const st = useRef({ timer: null, start: null, moved: false, mode: null, hover: -1, pointerId: null });

  const clear = () => { clearTimeout(st.current.timer); st.current.timer = null; };
  useEffect(() => () => clear(), []);
  const report = useCallback((o, h) => { if (onHold) onHold({ open: o, hover: h >= 0 ? REACTIONS[h] : null }); }, [onHold]);

  const show = (mode) => {
    clear();
    st.current.mode = mode;
    st.current.hover = -1;
    setHover(-1);
    setOpen(mode);
    if (mode === 'hold') { report(true, -1); try { navigator.vibrate && navigator.vibrate(12); } catch { /* not supported */ } }
  };
  const hide = () => {
    clear();
    const wasHold = st.current.mode === 'hold';
    st.current.mode = null;
    st.current.hover = -1;
    setOpen(null);
    setHover(-1);
    if (wasHold) report(false, -1);
  };
  const setH = (h) => {
    if (h === st.current.hover) return;
    st.current.hover = h;
    setHover(h);
    if (st.current.mode === 'hold') {
      report(true, h);
      if (h >= 0) { try { navigator.vibrate && navigator.vibrate(4); } catch { /* not supported */ } }
    }
  };

  const onPointerEnter = (e) => {
    if (e.pointerType !== 'mouse') return;
    if (st.current.mode === 'hover') { clear(); return; }
    clear();
    st.current.timer = setTimeout(() => show('hover'), HOVER_OPEN_MS);
  };
  const onPointerLeave = (e) => {
    if (e.pointerType !== 'mouse' || st.current.mode === 'hold') return;
    clear();
    if (st.current.mode === 'hover') st.current.timer = setTimeout(hide, HOVER_CLOSE_MS);
  };
  const onPointerDown = (e) => {
    if (e.button !== 0) return;
    st.current.start = { x: e.clientX, y: e.clientY };
    st.current.moved = false;
    st.current.pointerId = e.pointerId;
    try { btn.current && btn.current.setPointerCapture(e.pointerId); } catch { /* fine */ }
    if (st.current.mode === 'hover') return;   // the tray is already up — a click is a like
    clear();
    st.current.timer = setTimeout(() => show('hold'), HOLD_MS);
  };
  const onPointerMove = (e) => {
    const s = st.current;
    if (s.mode === 'hold') {
      const rect = tray.current ? tray.current.getBoundingClientRect() : null;
      setH(trayHit(e.clientX, e.clientY, rect ? { left: rect.left, top: rect.top, width: rect.width, height: rect.height } : null));
      return;
    }
    if (s.start && (Math.abs(e.clientX - s.start.x) > SLOP || Math.abs(e.clientY - s.start.y) > SLOP)) {
      s.moved = true;
      if (s.mode !== 'hover') clear();
    }
  };
  const onPointerUp = (e) => {
    const s = st.current;
    try { btn.current && btn.current.releasePointerCapture(e.pointerId); } catch { /* fine */ }
    if (s.mode === 'hold') {
      const h = s.hover;
      hide();
      if (h >= 0) onPick(REACTIONS[h]);
      s.start = null;
      return;
    }
    clear();
    const tap = s.start && !s.moved;
    s.start = null;
    if (tap) { if (s.mode === 'hover') hide(); onTap(); }
  };
  const onPointerCancel = () => { st.current.start = null; if (st.current.mode === 'hold') hide(); else clear(); };

  return (
    <span className={`rx-wrap ${placement === 'left' ? 'left' : ''}`}
      onPointerEnter={onPointerEnter} onPointerLeave={onPointerLeave}>
      {open && (
        <span ref={tray} className={`rx-tray ${dark ? 'dark' : ''} ${open === 'hold' ? 'held' : ''}`} role="menu"
          onPointerEnter={() => { if (st.current.mode === 'hover') clear(); }}>
          {REACTIONS.map((r, i) => (
            <button
              key={r} type="button" role="menuitem"
              className={`rx-face ${hover === i ? 'hot' : ''} ${hover >= 0 && hover !== i ? 'dim' : ''} ${mine === r ? 'mine' : ''}`}
              aria-label={REACTION_LABEL[r]}
              onPointerEnter={() => { if (st.current.mode === 'hover') setH(i); }}
              onPointerLeave={() => { if (st.current.mode === 'hover') setH(-1); }}
              onClick={(e) => { e.stopPropagation(); if (st.current.mode === 'hover') { hide(); onPick(r); } }}
            >
              {hover === i && <span className="rx-name">{REACTION_LABEL[r]}</span>}
              <ReactionAnim r={r} size={38} loop />
            </button>
          ))}
        </span>
      )}
      <button
        ref={btn} type="button"
        className={`rx-btn ${className}`}
        aria-label={label || (mine ? REACTION_LABEL[mine] : 'Like')}
        aria-haspopup="menu"
        onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerCancel}
        onContextMenu={(e) => e.preventDefault()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onTap(); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); show('hover'); }
          else if (e.key === 'Escape') hide();
        }}
      >
        {children}
      </button>
    </span>
  );
}

/** "Release to cancel" (or the face under the finger), laid over the action row. */
export function ReleaseToCancel({ hold }) {
  if (!hold || !hold.open) return null;
  return <div className="rx-release" aria-live="polite">{hold.hover ? REACTION_LABEL[hold.hover] : 'Release to cancel'}</div>;
}

/** The right of a post: the kinds people picked and how many. Opens the sheet. */
export function ReactionSummary({ post, onOpen, size = 18, className = '', showCount = true }) {
  const { tops, total } = reactionSummary(post);
  if (!total) return null;
  return (
    <button type="button" className={`rx-summary ${className}`} onClick={onOpen} title="See who reacted">
      <span className="rx-stack">
        {(tops.length ? tops : ['like']).map((r) => (
          <ReactionIcon key={r} r={r} size={size} />
        ))}
      </span>
      {showCount ? <span>{compactViews(total)}</span> : null}
    </button>
  );
}

/**
 * Who reacted — All, then a tab per kind (filtered by the server). Fifty at a
 * time; a person opens their profile.
 */
export function ReactionsSheet({ post, onClose, onProfile, me }) {
  const [kind, setKind] = useState(null);
  const [rows, setRows] = useState([]);
  const [next, setNext] = useState(null);
  const [busy, setBusy] = useState(true);
  const [totals, setTotals] = useState(null);
  const gen = useRef(0);
  const box = useRef(null);

  const load = useCallback(async (k, cursor) => {
    const mine = gen.current;
    setBusy(true);
    const r = await fetchLikers(post.id, cursor || null, k);
    if (mine !== gen.current) return;
    setBusy(false);
    if (!r.ok) { if (!cursor) setRows([]); setNext(null); return; }
    if (Array.isArray(r.reactions)) setTotals({ total: typeof r.total === 'number' ? r.total : null, reactions: r.reactions });
    setRows((cur) => {
      const seen = new Set(cursor ? cur.map((x) => x.uid) : []);
      const add = (r.people || []).filter((p) => p && p.uid && !seen.has(p.uid));
      return cursor ? cur.concat(add) : add;
    });
    setNext(r.next || null);
  }, [post.id]);

  useEffect(() => { gen.current += 1; setRows([]); setNext(null); load(kind, null); }, [kind, load]);
  useEffect(() => {
    const k = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose]);

  const kinds = totals && totals.reactions.length ? totals.reactions : reactionTabs(post).slice(1);
  const tabs = [{ r: null, n: (totals && totals.total != null) ? totals.total : reactionSummary(post).total }, ...kinds];

  /* 🟥 TO document.body — a sheet opened from the reels rail sits inside
     transformed boxes, and `position: fixed` under a transform is fixed to that
     box, not to the window. */
  return createPortal(
    <div className="rx-sheet-back" onClick={onClose} role="presentation">
      <div className="rx-sheet" role="dialog" aria-label="Reactions" onClick={(e) => e.stopPropagation()}>
        <header>
          <b>Reactions</b>
          <button type="button" className="rx-x" onClick={onClose} aria-label="Close"><X width={18} height={18} /></button>
        </header>
        {tabs.length > 1 && (
          <nav className="rx-tabs" role="tablist">
            {tabs.map((tb) => (
              <button key={tb.r || 'all'} type="button" role="tab" aria-selected={kind === tb.r}
                className={kind === tb.r ? 'on' : ''} onClick={() => setKind(tb.r)}>
                {tb.r ? <ReactionIcon r={tb.r} size={20} /> : <span>All</span>}
                <span className="n">{compactViews(tb.n)}</span>
              </button>
            ))}
          </nav>
        )}
        <div className="rx-list" ref={box}
          onScroll={(e) => {
            const el = e.currentTarget;
            if (!busy && next && el.scrollTop + el.clientHeight > el.scrollHeight - 300) load(kind, next);
          }}>
          {rows.map((p) => (
            <button key={p.uid} type="button" className="rx-row" onClick={() => onProfile && onProfile({ uid: p.uid, name: p.name, image: p.image })}>
              <span className="rx-face-wrap">
                <Pic src={p.image} name={p.name} size={40} />
                <ReactionIcon r={p.r || 'like'} size={18} className="rx-badge" />
              </span>
              <span className="rx-who">{p.name || 'Allo user'}{me && p.uid === me.uid ? ' (you)' : ''}</span>
            </button>
          ))}
          {busy && <div className="rx-more">Loading…</div>}
          {!busy && !rows.length && <div className="rx-more">{kind ? 'Nobody yet' : 'No reactions yet'}</div>}
          {!busy && !next && <div className="rx-credit">Animated emoji: Noto Emoji by Google · CC BY 4.0</div>}
        </div>
      </div>
    </div>,
    document.body,
  );
}
